import {useMergeboxStatusCalculator} from '@github-ui/mergebox/use-mergebox-status-calculator'
import {useHeaderPageData} from '../../../../page-data/loaders/use-header-page-data'

export type MergeBoxPullRequest = ReturnType<typeof useMergeboxStatusCalculator>['pullRequest']

/**
 * Wraps the merge-status calculator pipeline (which also wires up the relevant
 * live updates) and exposes the mergeability data that For You items derive their state from.
 *
 * Reads merge configuration from the live header query rather than pre-validating
 * it against the merge-status-button rules (open state, feature-flag opt-out).
 * Those rules exclude closed/merged pull requests, which some For You items still need.
 * Each item decides its own visibility from the relevant isClosedOrMerged state.
 * The calculator reads the current head SHA and channels from the live mergebox query.
 *
 * Returns `null` only when the header has no merge data at all, in which case
 * consumers should early return so the item is not registered as visible.
 */
export function useMergeabilityData() {
  const {mergeStatusButtonData} = useHeaderPageData()

  const {
    calculator,
    pullRequest: mergeBoxPullRequest,
    mergeRequirements,
    statusChecksData,
  } = useMergeboxStatusCalculator()

  if (!mergeStatusButtonData) return null

  return {
    defaultMergeMethod: mergeStatusButtonData.defaultMergeMethod,
    helpUrl: mergeStatusButtonData.helpUrl,
    mergeBoxPullRequest,
    mergeBoxRollupStatus: calculator.overallStatus,
    mergeRequirements,
    statusChecksData,
    statusChecks: statusChecksData.statusChecks,
    statusRollup: statusChecksData.statusRollup,
    checksSection: calculator.sections.ChecksSection,
    conflictsSection: calculator.sections.ConflictsSection,
    blockedSection: calculator.sections.BlockedSection,
    mergeQueueSection: calculator.sections.MergeQueueSection,
    mergeSection: calculator.sections.MergeSection,
    reviewerSection: calculator.sections.ReviewerSection,
    stackSection: calculator.sections.StackSection,
    stacksMergeConflictsAndRebaseSection: calculator.sections.StacksMergeConflictsAndRebaseSection,
    isClosedOrMerged: calculator.sections.ClosedOrMergedStateMergeBox.shouldRender,
    // `mergeStateStatus` is only ever `BEHIND` when the strict "must be up to date" status-checks
    // policy applies and the branch is genuinely stale (see PullRequest::MergeState#async_mergeable_rules_status
    // on the backend) — a non-strict policy or an up-to-date branch resolves to `:blocked` instead.
    // So `BEHIND` is already a reliable "update is required" signal on its own.
    requiresBranchUpdate: mergeBoxPullRequest.mergeStateStatus === 'BEHIND',
  }
}

export type MergeabilityData = NonNullable<ReturnType<typeof useMergeabilityData>>
